import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Share2, DollarSign } from "lucide-react";
import TransactionFeesInfo from "@/components/TransactionFeesInfo";

/**
 * Today's plans (until 31 October 2026), as the cards show them. The numbers match TODAY_PLANS in
 * src/content/pricing/plans.ts, which the /pricing structured data (Offer) is built from; seo tests check
 * the two agree. Words are in the locale files (pricing.*), so every site language renders the whole card.
 */
type PlanFeature = { key: string; values?: Record<string, string | number>; note?: string; fees?: boolean };

const plans: {
  id: "payg" | "lite" | "pro" | "enterprise";
  name: string;
  price: string;
  sharingFeatures: PlanFeature[];
  sellingFeatures: PlanFeature[];
  commonFeatures: PlanFeature[];
  featured: boolean;
}[] = [
  {
    id: "payg",
    name: "Pay As You Go",
    price: "$0",
    sharingFeatures: [{ key: "pricing.perPhotoUpload", values: { price: "$0.02" } }],
    sellingFeatures: [{ key: "pricing.commissionRate", values: { rate: "25%" }, note: "*", fees: true }],
    // On-demand FTP is on every plan (19 Oct 2026).
    commonFeatures: [{ key: "pricing.storageGb", values: { gb: 25 }, note: "**" }, { key: "pricing.photohawkDomain" }, { key: "pricing.onDemandFtp" }],
    featured: false,
  },
  {
    id: "lite",
    name: "Lite",
    price: "$16.99",
    sharingFeatures: [{ key: "pricing.perPhotoUpload", values: { price: "$0.0135" } }],
    sellingFeatures: [{ key: "pricing.commissionRate", values: { rate: "18.5%" }, note: "*", fees: true }],
    commonFeatures: [{ key: "pricing.storageGb", values: { gb: 100 }, note: "**" }, { key: "pricing.photohawkDomain" }, { key: "pricing.onDemandFtp" }],
    featured: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$29.99",
    sharingFeatures: [{ key: "pricing.perPhotoUpload", values: { price: "$0.010" } }],
    sellingFeatures: [{ key: "pricing.commissionRate", values: { rate: "16%" }, note: "*", fees: true }],
    commonFeatures: [
      { key: "pricing.storageGb", values: { gb: 500 }, note: "**" },
      { key: "pricing.customDomain" },
      { key: "pricing.analytics" },
      { key: "pricing.onDemandFtp" },
    ],
    featured: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "$44.99",
    sharingFeatures: [{ key: "pricing.perPhotoUpload", values: { price: "$0.008" } }],
    sellingFeatures: [{ key: "pricing.commissionRate", values: { rate: "13.5%" }, note: "*", fees: true }],
    commonFeatures: [
      { key: "pricing.storageGb", values: { gb: 1000 }, note: "**" },
      { key: "pricing.videoSupport" },
      { key: "pricing.managedSupport" },
    ],
    featured: false,
  },
];

const FeatureItem = ({ feature, featured }: { feature: PlanFeature; featured: boolean }) => {
  const { t } = useTranslation();
  return (
    <li className="flex items-start gap-3">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
        featured ? "bg-hawk-orange" : "bg-hawk-orange/10"
      }`}>
        <Check size={12} className={featured ? "text-white" : "text-hawk-orange"} />
      </div>
      <span className={`text-sm ${featured ? "text-white/90" : ""}`}>
        {t(feature.key, feature.values)}
        {feature.note && ` ${feature.note}`}
        {feature.fees && (
          <>
            {" "}
            <TransactionFeesInfo label={t("pricing.plusFees")} className="text-xs align-middle" onDark={featured} />
          </>
        )}
      </span>
    </li>
  );
};

/** `headingAs`: "h1" on the /pricing page (its only heading), "h2" where it's a section of another page. */
const PricingContent = ({ headingAs: Heading = "h2" }: { headingAs?: "h1" | "h2" }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="text-center max-w-3xl mx-auto mb-12">
        <span className="inline-block px-4 py-1.5 rounded-full bg-hawk-orange/10 text-hawk-orange text-sm font-semibold mb-4">
          {t("pricing.badge")}
        </span>
        <Heading className="text-4xl md:text-5xl font-display font-bold mb-6">
          {t("pricing.heading")}
        </Heading>
        <p className="text-lg text-muted-foreground">
          {t("pricing.subtitle")}
        </p>
      </div>

      {/* Mode Explanation */}
      <div className="flex flex-wrap justify-center gap-6 mb-12">
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary">
          <Share2 size={16} className="text-hawk-orange" />
          <span className="text-sm font-medium"><strong>{t("pricing.sharingMode")}</strong> — {t("pricing.sharingModeDesc")}</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary">
          <DollarSign size={16} className="text-hawk-orange" />
          <span className="text-sm font-medium"><strong>{t("pricing.sellingMode")}</strong> — {t("pricing.sellingModeDesc")}</span>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {plans.map((plan) => (
          <Card
            key={plan.name}
            variant={plan.featured ? "pricingFeatured" : "pricing"}
            className="relative"
          >
            {plan.featured && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-gradient-to-r from-hawk-orange to-hawk-gold text-white text-sm font-semibold flex items-center gap-1.5">
                <Sparkles size={14} />
                {t("pricing.mostPopular")}
              </div>
            )}

            <CardHeader className="pb-4">
              <CardTitle className={`text-lg ${plan.featured ? "text-white" : ""}`}>
                {plan.name}
              </CardTitle>
              <div className="mt-4">
                <span className={`text-4xl font-display font-bold ${plan.featured ? "text-white" : ""}`}>
                  {plan.price}
                </span>
                <span className={`text-sm ml-1 ${plan.featured ? "text-white/70" : "text-muted-foreground"}`}>
                  {t("pricing.perMonth")}
                </span>
              </div>
              <p className={`text-sm mt-2 ${plan.featured ? "text-white/70" : "text-muted-foreground"}`}>
                {t(`pricing.planDescriptions.${plan.id}`)}
              </p>
            </CardHeader>

            <CardContent>
              {/* Sharing Mode Features */}
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <Share2 size={14} className="text-hawk-orange" />
                  <span className={`text-xs font-semibold uppercase tracking-wide ${plan.featured ? "text-white/70" : "text-muted-foreground"}`}>{t("pricing.sharingMode")}</span>
                </div>
                <ul className="space-y-2">
                  {plan.sharingFeatures.map((feature) => (
                    <FeatureItem key={feature.key} feature={feature} featured={plan.featured} />
                  ))}
                </ul>
              </div>

              {/* Selling Mode Features */}
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <DollarSign size={14} className="text-hawk-orange" />
                  <span className={`text-xs font-semibold uppercase tracking-wide ${plan.featured ? "text-white/70" : "text-muted-foreground"}`}>{t("pricing.sellingMode")}</span>
                </div>
                <ul className="space-y-2">
                  {plan.sellingFeatures.map((feature) => (
                    <FeatureItem key={feature.key} feature={feature} featured={plan.featured} />
                  ))}
                </ul>
              </div>

              {/* Common Features */}
              <div className={`pt-4 border-t ${plan.featured ? "border-white/20" : "border-border"}`}>
                <span className={`text-xs font-semibold uppercase tracking-wide ${plan.featured ? "text-white/70" : "text-muted-foreground"}`}>{t("pricing.included")}</span>
                <ul className="space-y-2 mt-2 mb-6">
                  {plan.commonFeatures.map((feature) => (
                    <FeatureItem key={feature.key} feature={feature} featured={plan.featured} />
                  ))}
                </ul>
              </div>

              <Button
                variant={plan.featured ? "hero" : "outline"}
                className="w-full"
                asChild
              >
                {/* Descriptive link text (not four identical "Get Started" links). */}
                <Link to="/register">{t("pricing.startWithPlan", { plan: plan.name })}</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footnotes */}
      <div className="mt-10 max-w-3xl mx-auto px-4 space-y-3">
        <p className="text-center text-sm text-muted-foreground">{t("pricing.commissionNote")}</p>
        <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 flex items-start gap-3">
          <DollarSign size={16} className="text-hawk-orange flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t("pricing.processingFeesNote")}{" "}
            <TransactionFeesInfo label={t("pricing.viewTransactionFees")} className="text-xs" />
          </p>
        </div>
        <p className="text-center text-xs text-muted-foreground">{t("pricing.storageNote")}</p>
      </div>
    </>
  );
};

export default PricingContent;
